import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { LuDownload, LuTrash2, LuFileText, LuEye } from 'react-icons/lu';
import aviso, { textoDoErro, SEM_SERVIDOR } from '../../lib/aviso';
import RecordPanel, { PanelSection } from '../RecordPanel';
import Select from '../Select';
import Dialog from '../Dialog';
import {
    ModeloDocumentoService, NOME_TIPO_DOCUMENTO, NOME_STATUS_DOCUMENTO, StatusDocumento,
    type ModeloDocumento,
} from '../../services/ModeloDocumentoService';
import { DocumentoEmitidoService, type DocumentoEmitido, type ArquivoPdf } from '../../services/DocumentoEmitidoService';
import './DocumentosOportunidade.css';

interface Props {
    conversaId: number;
    /** O botão "Emitir documento" mora na tira de abas da página; ela abre o painel por aqui. */
    emitirAberto: boolean;
    onFecharEmitir: () => void;
}

const COLUNAS = '64px 92px minmax(0,2fr) 110px 100px 130px minmax(0,1fr) 72px';

const brl = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
const dataCurta = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');
/* A validade vem como data pura (yyyy-MM-dd); montar `Date` daria o dia anterior em fuso negativo. */
const dataPura = (d?: string | null) => {
    if (!d) return '—';
    const [a, m, dia] = d.slice(0, 10).split('-');
    return a && m && dia ? `${dia}/${m}/${a}` : d;
};
const numeroDoc = (n: number) => String(n).padStart(4, '0');

const COR_STATUS: Record<StatusDocumento, string> = {
    [StatusDocumento.Emitido]: 'var(--color-info)',
    [StatusDocumento.Aceito]: 'var(--color-success)',
    [StatusDocumento.Recusado]: 'var(--color-danger)',
    [StatusDocumento.Cancelado]: 'var(--color-neutral)',
};

const OPCOES_STATUS = (Object.values(StatusDocumento) as StatusDocumento[])
    .map(s => ({ value: String(s), label: NOME_STATUS_DOCUMENTO[s], dot: COR_STATUS[s] }));

/** Abre o PDF numa aba. A aba é aberta ANTES do fetch para o bloqueador de pop-up não barrar. */
const abrirPdf = (janela: Window | null, arquivo: ArquivoPdf) => {
    const url = URL.createObjectURL(arquivo.blob);
    if (janela) janela.location.href = url;
    else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

const salvarPdf = (arquivo: ArquivoPdf) => {
    const url = URL.createObjectURL(arquivo.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = arquivo.nome;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

/**
 * A aba "Documentos" da oportunidade: os PDFs emitidos a partir dos modelos
 * (proposta, orçamento, contrato) e o painel de emissão. Ver
 * docs/modelos-documento/especificacao.md §11. Nesta versão o sistema só gera:
 * o vendedor baixa e decide por onde manda.
 */
const DocumentosOportunidade: FC<Props> = ({ conversaId, emitirAberto, onFecharEmitir }) => {
    const [documentos, setDocumentos] = useState<DocumentoEmitido[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [modelos, setModelos] = useState<ModeloDocumento[]>([]);
    const [modeloId, setModeloId] = useState('');
    const [validadeDias, setValidadeDias] = useState('');
    const [titulo, setTitulo] = useState('');
    const [emitindo, setEmitindo] = useState(false);
    const [gerandoPrevia, setGerandoPrevia] = useState(false);
    const [baixando, setBaixando] = useState<number | null>(null);
    const [aRemover, setARemover] = useState<DocumentoEmitido | null>(null);
    const [removendo, setRemovendo] = useState(false);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const r = await DocumentoEmitidoService.listarPorConversa(conversaId);
            setDocumentos(r.dados || []);
        } catch (e) {
            aviso.erro(textoDoErro(e, SEM_SERVIDOR));
        } finally {
            setCarregando(false);
        }
    }, [conversaId]);

    useEffect(() => { carregar(); }, [carregar]);

    /* Os modelos ativos só são buscados quando o painel abre: a lista de
       documentos não precisa deles. */
    useEffect(() => {
        if (!emitirAberto) return;
        let vivo = true;
        ModeloDocumentoService.buscarTodos(undefined, true)
            .then(r => { if (vivo) setModelos(r.dados || []); })
            .catch(e => aviso.erro(textoDoErro(e, 'Não foi possível carregar os modelos.')));
        return () => { vivo = false; };
    }, [emitirAberto]);

    const modelo = useMemo(() => modelos.find(m => String(m.modeloDocumentoId) === modeloId) ?? null, [modelos, modeloId]);

    const escolherModelo = (v: string) => {
        setModeloId(v);
        const m = modelos.find(x => String(x.modeloDocumentoId) === v);
        setValidadeDias(m?.validadeDias ? String(m.validadeDias) : '');
    };

    const fecharPainel = () => {
        setModeloId(''); setValidadeDias(''); setTitulo('');
        onFecharEmitir();
    };

    const validadeNumero = (): number | null | undefined => {
        const t = validadeDias.trim();
        if (t === '') return null;
        const n = Number(t);
        return Number.isInteger(n) && n >= 0 ? n : undefined;
    };

    const verPrevia = async () => {
        if (!modelo) { aviso.atencao('Escolha um modelo.'); return; }
        const janela = window.open('', '_blank');
        setGerandoPrevia(true);
        try {
            abrirPdf(janela, await DocumentoEmitidoService.previa(modelo.modeloDocumentoId, conversaId));
        } catch (e) {
            janela?.close();
            aviso.erro(textoDoErro(e, 'Não foi possível gerar a prévia.'));
        } finally {
            setGerandoPrevia(false);
        }
    };

    const emitir = async () => {
        if (!modelo) { aviso.atencao('Escolha um modelo.'); return; }
        const validade = validadeNumero();
        if (validade === undefined) { aviso.atencao('A validade deve ser um número de dias.'); return; }
        setEmitindo(true);
        try {
            const r = await DocumentoEmitidoService.emitir({
                conversaId,
                modeloDocumentoId: modelo.modeloDocumentoId,
                /* Em branco = validade do modelo; zero = sem validade. */
                validadeDias: validade === null ? null : validade === 0 ? 0 : validade,
                titulo: titulo.trim() || null,
            });
            aviso.ok(`${NOME_TIPO_DOCUMENTO[r.dados.tipo]} nº ${numeroDoc(r.dados.numero)} emitida.`);
            fecharPainel();
            await carregar();
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível emitir o documento.'));
        } finally {
            setEmitindo(false);
        }
    };

    const baixar = async (d: DocumentoEmitido, abrir: boolean) => {
        const janela = abrir ? window.open('', '_blank') : null;
        setBaixando(d.documentoEmitidoId);
        try {
            const arquivo = await DocumentoEmitidoService.pdf(d.documentoEmitidoId);
            if (abrir) abrirPdf(janela, arquivo); else salvarPdf(arquivo);
        } catch (e) {
            janela?.close();
            aviso.erro(textoDoErro(e, 'Não foi possível obter o PDF.'));
        } finally {
            setBaixando(null);
        }
    };

    const mudarStatus = async (d: DocumentoEmitido, v: string) => {
        const status = Number(v) as StatusDocumento;
        if (status === d.status) return;
        const anterior = documentos;
        setDocumentos(ds => ds.map(x => x.documentoEmitidoId === d.documentoEmitidoId ? { ...x, status } : x));
        try {
            await DocumentoEmitidoService.atualizarStatus(d.documentoEmitidoId, status);
        } catch (e) {
            setDocumentos(anterior);
            aviso.erro(textoDoErro(e, 'Não foi possível atualizar o status.'));
        }
    };

    const remover = async () => {
        if (!aRemover) return;
        setRemovendo(true);
        try {
            await DocumentoEmitidoService.remover(aRemover.documentoEmitidoId);
            aviso.ok('Documento removido.');
            setARemover(null);
            await carregar();
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível remover o documento.'));
        } finally {
            setRemovendo(false);
        }
    };

    return (
        <>
            <div className="dd-pane-body">
                {carregando ? (
                    [1, 2].map(i => <div key={i} className="pl-skel" style={{ height: 40, marginBottom: 8 }} />)
                ) : documentos.length > 0 ? (
                    <div className="lk-table do-table">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Nº</div><div className="lk-head">Tipo</div><div className="lk-head">Título</div>
                            <div className="lk-head">Total</div><div className="lk-head">Válido até</div><div className="lk-head">Status</div>
                            <div className="lk-head">Emitido por</div><div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {documentos.map(d => (
                                <div key={d.documentoEmitidoId} className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                                    <span className="lk-cell lk-muted"><span>{numeroDoc(d.numero)}</span></span>
                                    <span className="lk-cell lk-muted"><span>{NOME_TIPO_DOCUMENTO[d.tipo]}</span></span>
                                    <span className="lk-cell lk-strong">
                                        <button type="button" className="do-titulo" title="Abrir o PDF"
                                            onClick={() => baixar(d, true)} disabled={baixando === d.documentoEmitidoId}>
                                            {d.titulo}
                                        </button>
                                    </span>
                                    <span className="lk-cell"><span className="pl-card-val">{brl(d.valorTotal)}</span></span>
                                    <span className="lk-cell lk-muted"><span>{dataPura(d.validade)}</span></span>
                                    <span className="lk-cell do-status">
                                        <Select size="sm" portal showOptionAccessory ariaLabel="Status do documento"
                                            value={String(d.status)} options={OPCOES_STATUS} onChange={v => mudarStatus(d, v)} />
                                    </span>
                                    <span className="lk-cell lk-muted">
                                        <span title={dataCurta(d.dataEmissao)}>{d.nomeUsuarioEmissor || '—'} · {dataCurta(d.dataEmissao)}</span>
                                    </span>
                                    <span className="lk-cell lk-acts-cell">
                                        <button type="button" className="lk-act" aria-label="Ver PDF" title="Ver PDF"
                                            onClick={() => baixar(d, true)} disabled={baixando === d.documentoEmitidoId}><LuEye size={14} /></button>
                                        <button type="button" className="lk-act" aria-label="Baixar PDF" title="Baixar PDF"
                                            onClick={() => baixar(d, false)} disabled={baixando === d.documentoEmitidoId}><LuDownload size={14} /></button>
                                        <button type="button" className="lk-act is-danger" aria-label="Remover documento" title="Remover documento"
                                            onClick={() => setARemover(d)}><LuTrash2 size={14} /></button>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="dd-blank">
                        <LuFileText className="dd-blank-icon" size={26} aria-hidden="true" />
                        <div className="dd-blank-title">Nenhum documento emitido</div>
                        <p className="dd-blank-text">
                            Emita uma proposta, um orçamento ou um contrato a partir de um modelo. O PDF sai com os dados
                            do contato e os itens desta oportunidade, e fica guardado aqui.
                        </p>
                    </div>
                )}
            </div>

            <RecordPanel
                open={emitirAberto}
                onClose={fecharPainel}
                title="Emitir documento"
                editing
                footer={
                    <>
                        <button type="button" className="rp-btn" onClick={fecharPainel} disabled={emitindo || gerandoPrevia}>Cancelar</button>
                        <button type="button" className="rp-btn" onClick={verPrevia} disabled={!modelo || emitindo || gerandoPrevia}>
                            {gerandoPrevia ? 'Gerando…' : 'Ver prévia'}
                        </button>
                        <button type="button" className="rp-btn is-primary" onClick={emitir} disabled={!modelo || emitindo || gerandoPrevia}>
                            {emitindo ? 'Emitindo…' : 'Emitir'}
                        </button>
                    </>
                }
            >
                <PanelSection title="Modelo">
                    <div className="rp-field">
                        <label className="rp-label">Modelo *</label>
                        <Select
                            value={modeloId}
                            onChange={escolherModelo}
                            placeholder={modelos.length ? 'Escolher o modelo' : 'Nenhum modelo ativo'}
                            options={modelos.map(m => ({
                                value: String(m.modeloDocumentoId), label: m.nome, meta: NOME_TIPO_DOCUMENTO[m.tipo],
                            }))}
                            searchFrom={8}
                        />
                        {modelos.length === 0 && (
                            <span className="rp-hint">Cadastre um modelo em Configurações › Modelos de documento.</span>
                        )}
                    </div>
                </PanelSection>
                <PanelSection title="Documento">
                    <div className="rp-field">
                        <label className="rp-label" htmlFor="do-validade">Validade (dias)</label>
                        <input id="do-validade" type="number" min={0} className="rp-input" value={validadeDias}
                            placeholder="Sem validade" onChange={e => setValidadeDias(e.target.value)} />
                        <span className="rp-hint">Contada a partir de hoje. Em branco ou zero emite sem validade.</span>
                    </div>
                    <div className="rp-field">
                        <label className="rp-label" htmlFor="do-titulo">Título</label>
                        <input id="do-titulo" type="text" className="rp-input" value={titulo} maxLength={200}
                            placeholder={modelo ? `${NOME_TIPO_DOCUMENTO[modelo.tipo]} nº … - nome do cliente` : 'Gerado a partir do número'}
                            onChange={e => setTitulo(e.target.value)} />
                        <span className="rp-hint">Em branco, o sistema monta com o tipo, o número e o nome do cliente.</span>
                    </div>
                </PanelSection>
            </RecordPanel>

            <Dialog
                open={aRemover !== null}
                onClose={() => setARemover(null)}
                title={aRemover ? `Remover "${aRemover.titulo}"?` : ''}
                confirmLabel="Remover"
                cancelLabel="Manter"
                danger
                busy={removendo}
                onConfirm={remover}
            >
                O documento sai desta lista. O número não é reaproveitado.
            </Dialog>
        </>
    );
};

export default DocumentosOportunidade;
